import React from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  withStyles,
  createStyles,
  Box,
  makeStyles,
  Button
} from "@material-ui/core";
import { colors, formatTimestamp, Loader } from "swyft-react-core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

declare type RowId = string | number;

interface ObjectWithId {
  id: RowId;
}

interface RowData extends ObjectWithId {
  [key: string]: any;
}

declare type RowsProp = RowData[];

declare type NativeColTypes = "string" | "number" | "date" | "dateTime";
declare type ColType = NativeColTypes | string;

declare type Columns = ColDef[];
export interface ColDef {
  field: string;
  headerName?: string;
  width?: number;
  hidden?: boolean;
  sortName?: string;
  type?: ColType;
}

export interface TableProps {
  isLoading: boolean;

  rows: RowsProp;
  columns: Columns;

  handleRowClick?: (id: string | number) => void;

  sort: string;
  sortAscending: boolean;
  handleSort: (field: string) => void;

  page: number;
  pageCount: number;
  handlePageChange: (page: number) => void;
}

export const SwyftTable = React.forwardRef<HTMLDivElement, TableProps>(function DataGrid(props, ref) {
  const {
    rows,
    columns,
    handleRowClick,
    sort,
    sortAscending,
    handleSort,
    page,
    pageCount,
    handlePageChange,
    isLoading
  } = props;

  var displayedColumns = columns.filter((c) => c.hidden !== true);
  var displayedColumnFieldNames = displayedColumns.map((c) => c.field);

  const pagination = (
    <Box display="flex" justifyContent="space-between" my={2}>
      <Button onClick={() => handlePageChange(page - 1)} disabled={isLoading || page <= 1}>
        <ChevronLeftIcon /> Prev
      </Button>
      <Button
        onClick={() => handlePageChange(page + 1)}
        disabled={isLoading || (pageCount > 0 ? page >= pageCount : rows.length < 1)}
      >
        Next <ChevronRightIcon />
      </Button>
    </Box>
  );

  return (
    <React.Fragment>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {displayedColumns.map((column, i) => (
                <HeaderCell key={i} column={column} sort={sort} asc={sortAscending} sortHandler={handleSort} />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={displayedColumns.length}>
                  <Loader />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                var displayedCells = Object.keys(row).filter((k) => displayedColumnFieldNames.includes(k));

                return (
                  <StyledTableRow
                    key={row.id}
                    onClick={(e) => (handleRowClick != null ? handleRowClick(row.id) : e.stopPropagation())}
                  >
                    {displayedCells.map((value, i) => {
                      var col = columns.find((c) => c.field === value);

                      var displayVal = row[value];

                      if (col!.type === "date") {
                        displayVal = formatTimestamp(displayVal, false);
                      } else if (col!.type === "dateTime") {
                        displayVal = formatTimestamp(displayVal, true);
                      }

                      return <StyledTableCell key={i}>{displayVal}</StyledTableCell>;
                    })}
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
    </React.Fragment>
  );
});

const headerStyles = makeStyles(() => ({
  sortIcon: {
    marginLeft: "3px",
    height: 24,
    cursor: "pointer"
  }
}));

interface HeaderProps {
  column: ColDef;
  sort?: string;
  asc: boolean;
  sortHandler: (fieldName: string) => void;
}

const HeaderCell: React.FC<HeaderProps> = ({ column, sort, asc, sortHandler }) => {
  const classes = headerStyles();
  return (
    <StyledTableCell onClick={() => (column.sortName ? sortHandler(column.sortName) : {})}>
      <Box display="flex">
        <Box>{column.headerName || ""}</Box>
        <Box className={classes.sortIcon}>
          {column.sortName &&
            (column.sortName === sort ? (
              asc ? (
                <ArrowDropUpIcon htmlColor={colors.secondary} />
              ) : (
                <ArrowDropDownIcon htmlColor={colors.secondary} />
              )
            ) : (
              <ArrowRightIcon />
            ))}
        </Box>
      </Box>
    </StyledTableCell>
  );
};

const StyledTableRow = withStyles(() =>
  createStyles({
    root: {
      "&:hover": {
        backgroundColor: colors.lighterSecondary
      }
    }
  })
)(TableRow);

const StyledTableCell = withStyles(() =>
  createStyles({
    head: {
      fontWeight: "bold"
    }
  })
)(TableCell);
